import type { BrandingDocument } from '../../../wire';

/**
 * **Where an unpublished draft is kept: this browser.**
 *
 * `admin-api` has no draft storage — a branding row is what is published and
 * its five-deep history is what was — and adding one is a schema change with
 * a retention story of its own. So the builder autosaves to `localStorage`,
 * per business, and says so in words on the top bar ("Draft saved on this
 * computer"): a draft does not follow the operator to another machine, and a
 * second operator does not see it. Publishing is the only way a change leaves
 * this browser, and it never happens on autosave.
 *
 * Every read and write is wrapped: a private window, blocked site data or a
 * full quota is a builder that still works and only forgets on reload.
 */

export interface StoredDraft {
  /** The published version this draft was begun from. */
  base_version: number;
  document: BrandingDocument;
  /** ISO time of the last save. */
  saved_at: string;
}

const PREFIX = 'yourstore.builder.draft.';

export function loadDraft(tenantId: string): StoredDraft | null {
  try {
    const raw = window.localStorage.getItem(PREFIX + tenantId);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<StoredDraft>;
    if (
      typeof parsed.base_version !== 'number' ||
      typeof parsed.saved_at !== 'string' ||
      parsed.document === null ||
      typeof parsed.document !== 'object'
    ) {
      return null;
    }
    return parsed as StoredDraft;
  } catch {
    return null;
  }
}

/** `true` when the draft is now on this computer. */
export function saveDraft(tenantId: string, draft: StoredDraft): boolean {
  try {
    window.localStorage.setItem(PREFIX + tenantId, JSON.stringify(draft));
    return true;
  } catch {
    return false;
  }
}

export function clearDraft(tenantId: string): void {
  try {
    window.localStorage.removeItem(PREFIX + tenantId);
  } catch {
    // Nothing to do: a store that cannot be written holds nothing to clear.
  }
}
